import {
  AccordionDetails,
  Box,
  Button,
  Grid,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { useState, useRef, useContext } from 'react';

import { StatusCode } from '../../../../../../api/enumerations';
import { deleteCoverage } from '../../../../../../api/theHive/noticeCoverage';
import { CoverageData } from '../../../../../../api/theHive/noticeCoverage/types';
import {
  IconExpandMoreMS,
  IconDeleteMS,
} from '../../../../../../constants/icons';
import { GlobalContext } from '../../../../../../context/global';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import { ModalAddingCoverage } from './ModalAddingCoverage';
import {
  CoverageContainer,
  CityText,
  StyledAccordionSummary,
  StyledAccordion,
  AccordionTitle,
  DisplacementText,
  DisplacementValue,
  ContentContainer,
  ScrollableGrid,
} from './styles';

interface CoverageAreaProps {
  data: CoverageData[];
  fetchCoverage: () => Promise<void>;
}

export function CoverageAreaSection({
  data,
  fetchCoverage,
}: CoverageAreaProps): JSX.Element {
  const [showCoverageModal, setShowCoverageModal] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const scrollableGridRef = useRef<HTMLDivElement>(null);

  const { getErrorMessage } = useErrorMessage();
  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);

  const handleAccordionChange =
    (id: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? id : null);
    };

  const handleDelete = async (
    event: React.MouseEvent,
    id: number
  ): Promise<void> => {
    event.stopPropagation();
    setDeletingId(id);

    try {
      const response = await deleteCoverage(id);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');

      fetchCoverage();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <CoverageContainer item container xs={12}>
      <Grid
        item
        xs={12}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography fontSize={21}>Área de cobertura</Typography>
        <Button onClick={() => setShowCoverageModal(true)}>
          adicionar cobertura
        </Button>
      </Grid>
      <ScrollableGrid item container mt={2} ref={scrollableGridRef}>
        {data.length === 0 ? (
          <Box mt={2}>
            <Typography>Nenhuma área de cobertura adicionada</Typography>
          </Box>
        ) : (
          data.map((region) =>
            deletingId === region.id ? (
              <Skeleton
                key={region.id}
                style={{
                  backgroundColor: '#fafcff',
                  boxShadow: '0px 3px 6px #00000029',
                }}
                variant="rectangular"
                width="98%"
                height={45}
                sx={{ mb: 2, borderRadius: 1 }}
              />
            ) : (
              <StyledAccordion
                key={region.id}
                expanded={expanded === region.id}
                onChange={handleAccordionChange(region.id)}
              >
                <StyledAccordionSummary expandIcon={null}>
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Grid item xs display="flex" alignItems="center">
                      <AccordionTitle>{region.name}</AccordionTitle>
                      <span className="expandIcon">{IconExpandMoreMS}</span>
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={(e) => handleDelete(e, region.id)}
                        disabled={deletingId === region.id}
                        color="primary"
                        sx={{ '&:hover': { color: 'secondary.main' } }}
                      >
                        {IconDeleteMS}
                      </IconButton>
                    </Grid>
                  </Grid>
                </StyledAccordionSummary>
                <AccordionDetails>
                  <ContentContainer>
                    <AccordionTitle mb={1}>{region.city.name}</AccordionTitle>
                    <DisplacementText>
                      Valor do deslocamento:{' '}
                      <DisplacementValue>
                        R${' '}
                        {region.displacement_fee
                          ? region.displacement_fee.toFixed(2)
                          : ''}
                      </DisplacementValue>
                    </DisplacementText>
                    <Grid item container direction="column">
                      {region.subregions?.map((subregion) => (
                        <Grid item key={subregion.id} mt={0.6}>
                          <CityText>{subregion.city.name}</CityText>
                        </Grid>
                      ))}
                    </Grid>
                  </ContentContainer>
                </AccordionDetails>
              </StyledAccordion>
            )
          )
        )}
      </ScrollableGrid>
      <ModalAddingCoverage
        isModalOpen={showCoverageModal}
        onCloseModal={() => setShowCoverageModal(false)}
        title="Área de cobertura"
        fetchCoverage={fetchCoverage}
      />
    </CoverageContainer>
  );
}
